/**
 * Capturing tgWebAppStartParam out of window.location.
 *
 * This module must be imported first at the entry point: @telegram-apps/sdk
 * reloads the page to strip the launch params from the URL, and after that the
 * parameter can no longer be read from location. A router could rewrite the
 * address too.
 *
 * The known caching bugs in Telegram clients (tdesktop#28303,
 * telegram-apps#646) concern performance.getEntriesByType('navigation') and the
 * raw initData kept in sessionStorage — not window.location. So the token is
 * read from here, while "who is this" comes separately from signed initData.
 */

export type LaunchIntent =
  | { kind: 'attendance'; token: string }
  | { kind: 'invite'; inviteCode: string }
  /** Arrived from the bot's reminder: they want the schedule, not a check-in. */
  | { kind: 'schedule' }
  | { kind: 'unknown'; raw: string }
  | { kind: 'none' };

/** The start_param alphabet: A-Z a-z 0-9 _ - , up to 512 characters. */
const START_PARAM = /^[\w-]{1,512}$/;

function readRawStartParam(href: string): string | null {
  const url = new URL(href);
  // Telegram clients pass launch params sometimes in the hash and sometimes in
  // the query — read both.
  const fromQuery = url.searchParams.get('tgWebAppStartParam');
  if (fromQuery !== null) return fromQuery;
  const hash = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
  return new URLSearchParams(hash).get('tgWebAppStartParam');
}

export function parseStartParam(raw: string | null): LaunchIntent {
  if (raw === null || raw === '') return { kind: 'none' };
  if (!START_PARAM.test(raw)) return { kind: 'unknown', raw };
  // Non-overlapping prefixes; anything unrecognised is rejected.
  //
  // Two of them check in. `a_` is the rehearsal's own code off the regent's
  // panel, `p_` the one a singer who is already marked shows to the next one -
  // and /attendance/self tells them apart by that prefix itself, so the token
  // travels whole. Both are the same intent here: what the singer did was scan
  // a code, and which screen it came off is the server's question.
  if (raw.startsWith('a_') || raw.startsWith('p_')) return { kind: 'attendance', token: raw };
  if (raw.startsWith('j_')) return { kind: 'invite', inviteCode: raw.slice(2) };
  if (raw === 'schedule') return { kind: 'schedule' };
  return { kind: 'unknown', raw };
}

export function readLaunchIntent(): LaunchIntent {
  return parseStartParam(readRawStartParam(window.location.href));
}

/** The value as of page load. Survives the SDK's reload. */
export const initialLaunchIntent: LaunchIntent = readLaunchIntent();

/**
 * The token out of text read by the native scanner: what it returns is the whole
 * deep link `https://t.me/<bot>?startapp=a_...`, not the token itself. `p_...`
 * arrives the same way and is the same intent - see parseStartParam.
 */
export function tokenFromScannedText(text: string): string | null {
  const trimmed = text.trim();
  const direct = parseStartParam(trimmed);
  if (direct.kind === 'attendance') return direct.token;
  try {
    const param = new URL(trimmed).searchParams.get('startapp');
    const parsed = parseStartParam(param);
    return parsed.kind === 'attendance' ? parsed.token : null;
  } catch {
    return null;
  }
}
